import { Component, effect, inject, untracked } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Sidebar } from './components/sidebar/sidebar';
import { ToastModule } from 'primeng/toast';
import { AccountService } from './services/account.service';
import { KeycloakService } from './services/keycloak.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastService } from './services/toast.service';
import { ONBOARDING_STATUS } from './enum/onboarding';
import { ButtonModule } from 'primeng/button';
import { OnboardingnService } from './services/onboarding.service';
import { ConfirmationService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Sidebar, ToastModule, ConfirmDialogModule, ButtonModule],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {
  private accountService = inject(AccountService);
  private keycloakService = inject(KeycloakService);
  private onboardingService = inject(OnboardingnService);
  private toastService = inject(ToastService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  private ONBOARDING_TOAST_KEY = 'onboarding';

  constructor() {
    effect(() => {
      if (this.keycloakService.isAuthenticated()) {
        const email = this.keycloakService.getUserEmail();
        if (email) {
          untracked(() => {
            this.accountService.loadProviderStatus(email);
            this.isLastTermOfUseAccepted();
            this.getOnboardingStatus();
          });
        }
      }
    });
  }

  // Pages affichées sans layout (barre latérale) : celles dont la route porte data.hideLayout = true
  protected hideLayout = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) route = route.firstChild;
        return route.data['hideLayout'] === true;
      })
    ),
    { initialValue: false }
  );

  private isLastTermOfUseAccepted(): void {
    this.accountService.isLastTermOfUseAccepted().subscribe({
      next: (isAccepted) => {
        if (isAccepted === false) this.acceptTermOfUse();
      },
      error: () => {
        this.toastService.error(
          "Erreur lors de la vérification de l'acceptation des CGU"
        )
      },
    });
  }

  private getOnboardingStatus(): void {
    this.onboardingService.onboardingState().subscribe({
      next: (response) => {
        if (!response) return;

        switch (response.onboardingStatus) {
          case ONBOARDING_STATUS.INCOMPLETE:
            this.toastService.error(
              "Info",
              undefined,
              "Onboarding incomplet ? Cliquez ici pour générer une nouvelle session",
              this.ONBOARDING_TOAST_KEY,
              true
            )
            break;
          case ONBOARDING_STATUS.PENDING:
            this.toastService.info(
              "Votre dossier a bien été transmis à Stripe et est actuellement en cours de vérification."
            )
            break;
          default:
            this.onboardingService.onboardingComplete().subscribe({
              error: () => {
                this.toastService.error(
                  "Erreur lors du marquage de l'onboarding comme complet"
                )
              }
            });
            break;
        }
      },
      error: () => {
        this.toastService.error("Erreur lors de la récupération de l'état de l'onboarding");
      },
    });
  }

  protected resumeOnboarding(): void {
    this.onboardingService.createNewOnboardingSession().subscribe({
      next: () => {
        this.toastService.clear(this.ONBOARDING_TOAST_KEY)
        this.toastService.success("Session d'onboarding réinitialisée");
      },
      error: () => {
        this.toastService.error("Une erreur est survenue lors de la création d'une nouvelle session d'onboarding. Veuillez réessayer.");
      },
    });
  }

  private acceptTermOfUse() {
    this.confirmationService.confirm({
      message: `
              Nos 
              <a href="https://www.wewiins.fr/cgu-btoc/" target="_blank">
                conditions générales d'utilisation
              </a> ont été mises à jour. Vous devez les accepter pour continuer à utiliser l'application.
            `,
      header: 'Mise à jour des CGU',
      rejectVisible: false,
      closeOnEscape: false,
      dismissableMask: false,
      blockScroll: true,
      closable: false,
      modal: true,
      acceptButtonProps: {
        label: "J'accepte les CGU",
        styleClass: 'primary-btn'
      },
      accept: () => {
        this.accountService.acceptTermOfUse().subscribe({
          next: () => {
            this.toastService.success("Confirmation de l'acceptation des CGU");
          },
          error: () => {
            this.toastService.error("Une erreur est survenue lors de l'acceptation des CGU");
          },
        });
      },
      reject: () => { }
    });
  }
}